import { cn } from '@change/ds-utils';

import { ModuleSeparator } from 'src/app/pages/starterDashboardV2/shared/components';
import { useDynamicModules } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { NextSteps } from '../NextSteps';

import { type AnimationPhase, useAnimationState } from './hooks/useAnimationState';

type NextStepsSectionProps = {
	position: 'top' | 'bottom';
	shouldShow: boolean;
	allTasksCompleted: boolean;
};

type AnimatedWrapperProps = {
	children: React.ReactNode;
	position: 'top' | 'bottom';
	phase: AnimationPhase;
};

function AnimatedWrapper({ children, position, phase }: AnimatedWrapperProps) {
	const isEntering = phase === 'entering';
	const isExiting = phase === 'exiting';

	const getTranslateClass = () => {
		if (position === 'top' && isExiting) return 'motion-safe:translate-y-5';
		if (position === 'bottom' && isEntering) return 'motion-safe:-translate-y-5';
		return 'motion-safe:translate-y-0';
	};

	const getOpacityClass = () => {
		if (isExiting || (position === 'bottom' && isEntering)) return 'motion-safe:opacity-0';
		return 'motion-safe:opacity-100';
	};

	return (
		<div
			className={cn(
				'motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-in-out',
				getTranslateClass(),
				getOpacityClass(),
			)}
		>
			{children}
		</div>
	);
}

export function NextStepsSection({ position, shouldShow, allTasksCompleted }: NextStepsSectionProps) {
	const { isDynamicModuleLoaded } = useDynamicModules();
	const { isRendered, phase } = useAnimationState(shouldShow);

	const renderContent = () => (
		<AnimatedWrapper position={position} phase={phase}>
			<NextSteps />
		</AnimatedWrapper>
	);

	if (position === 'top') {
		return (
			<>
				{isRendered && renderContent()}
				{!allTasksCompleted && isDynamicModuleLoaded('NextSteps') && (
					<ModuleSeparator data-testid="next-steps-separator" />
				)}
			</>
		);
	}

	return (
		<>
			{allTasksCompleted && isDynamicModuleLoaded('NextSteps') && (
				<ModuleSeparator data-testid="next-steps-bottom-separator" />
			)}
			{isRendered && renderContent()}
		</>
	);
}
